import { useCallback, useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { doc, getDoc, updateDoc, runTransaction, Timestamp } from 'firebase/firestore';
import { db, auth } from 'src/firebase/firebaseConfig';
import { ClientField, ClientFormInstance } from 'shared/types/appTypes';
import { useNotifications } from '@toolpad/core';

/** Value equality for saved answers; Timestamps compare by instant. */
const sameFieldValue = (a: unknown, b: unknown): boolean => {
  if (a instanceof Timestamp && b instanceof Timestamp) return a.toMillis() === b.toMillis();
  return JSON.stringify(a) === JSON.stringify(b);
};

/** The question at the saved index is no longer the one on screen, or it was finalized meanwhile. */
class FormChangedError extends Error {
  constructor(public readonly reason: 'changed' | 'finalized' = 'changed') {
    super(reason === 'finalized' ? 'Client form field was finalized' : 'Client form changed since it was loaded');
    this.name = 'FormChangedError';
  }
}

export interface ClientFormValues {
  fields: Record<string, any>;
}

export interface UseClientFormReturn {
  form: UseFormReturn<ClientFormValues>;
  fields: ClientField[];
  isLoading: boolean;
  isSaving: boolean;
  saveField: (fieldIndex: number) => Promise<void>;
  getFieldValue: (fieldIndex: number) => any;
  setFieldValue: (fieldIndex: number, value: any) => void;
}

export const useClientForm = (eventId: string | undefined): UseClientFormReturn => {
  const [fields, setFields] = useState<ClientField[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const notifications = useNotifications();

  const form = useForm<ClientFormValues>({
    defaultValues: {
      fields: {},
    },
  });

  // Load fields from Firebase
  useEffect(() => {
    const loadFields = async () => {
      if (!eventId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const docRef = doc(db, 'clientForms', eventId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data() as ClientFormInstance;

          // Record that the client opened the form
          try {
            await updateDoc(docRef, {
              clientLastOpenedAt: Timestamp.now(),
            });
          } catch (e) {
            console.error('Error recording clientLastOpenedAt:', e);
          }

          const loadedFields = data.fields || [];
          const formValues: Record<string, any> = {};

          loadedFields.forEach((field, index) => {
            // Video fields don't have a value property
            if (field.type !== 'video' && 'value' in field) {
              formValues[index] = field.value;
            }
          });

          setFields(loadedFields);
          form.reset({ fields: formValues });
        } else {
          setFields([]);
        }
      } catch (error) {
        console.error('Error loading client form fields:', error);
        notifications.show('Failed to load form fields', { severity: 'error', autoHideDuration: 3000 });
      } finally {
        setIsLoading(false);
      }
    };

    loadFields();
  }, [eventId, form, notifications]);

  // Save a single field to Firebase
  const saveField = useCallback(async (fieldIndex: number) => {
    if (!eventId || !fields[fieldIndex]) return;

    const field = fields[fieldIndex];

    // Prevent saving finalized fields
    if (field.type !== 'video' && 'finalized' in field && field.finalized) return;

    const value = form.getValues(`fields.${fieldIndex}`);

    try {
      setIsSaving(true);

      // Merge only this question into the CURRENT server fields, in a transaction. Writing the locally
      // cached array would undo server-side changes made since the form opened (e.g. the event-date
      // sync moving an untouched auto-populated date). See
      // docs/work/2026-11/2026-11-02-meeting-couple-glitches/couple-glitches-review.md (F03).
      const docRef = doc(db, 'clientForms', eventId);
      const { fields: serverFields, keptServerValue } = await runTransaction(db, async (tx) => {
        const snap = await tx.get(docRef);
        if (!snap.exists()) throw new FormChangedError();
        const current: ClientField[] = Array.isArray(snap.data()?.fields) ? [...(snap.data() as ClientFormInstance).fields] : [];
        const serverField = current[fieldIndex];

        // The question at this index must still be the one the client is answering.
        if (!serverField || serverField.type !== field.type || serverField.label !== field.label) {
          throw new FormChangedError();
        }
        if (serverField.type !== 'video' && serverField.finalized) throw new FormChangedError('finalized');

        const currentTimestamp = Timestamp.now();
        const currentUserEmail = auth.currentUser?.email || '';

        if (serverField.type === 'video') {
          current[fieldIndex] = {
            ...serverField,
            modifiedAt: currentTimestamp,
            modifiedBy: currentUserEmail,
          };
        } else {
          // No real client edit (the value is still what this client loaded) but the server has moved it
          // since (e.g. the event-date sync): keep the server's value and baseline. Writing it back would
          // restore the stale value and mark it client-edited, so it would stop following the event.
          const loadedValue = 'value' in field ? field.value : undefined;
          const serverValue = 'value' in serverField ? serverField.value : undefined;
          if (sameFieldValue(value, loadedValue) && !sameFieldValue(serverValue, loadedValue)) {
            return { fields: current, keptServerValue: true };
          }

          // Edit history is built against the server's current value, not the cached one.
          const previousValue = 'value' in serverField ? serverField.value : null;
          let updatedEditHistory = serverField.editHistory || [];
          if (JSON.stringify(previousValue) !== JSON.stringify(value)) {
            updatedEditHistory = [
              ...updatedEditHistory,
              {
                modifiedAt: currentTimestamp,
                modifiedBy: currentUserEmail,
                previousValue,
              },
            ];
          }

          // Client-owned keys only; everything else (incl. autoPopulatedValue) stays as the server has it.
          current[fieldIndex] = {
            ...serverField,
            value,
            modifiedAt: currentTimestamp,
            modifiedBy: currentUserEmail,
            editHistory: updatedEditHistory,
            // Mark confirmed if this is an auto-populated field
            ...('autoPopulatedValue' in serverField && serverField.autoPopulatedValue !== undefined
              ? { clientConfirmed: true }
              : {}),
          } as ClientField;
        }

        tx.update(docRef, {
          fields: current,
          updatedAt: currentTimestamp,
          updatedBy: currentUserEmail,
        });
        return { fields: current, keptServerValue: false };
      });

      if (keptServerValue) {
        const serverField = serverFields[fieldIndex];
        if ('value' in serverField) form.setValue(`fields.${fieldIndex}`, serverField.value);
        notifications.show('This answer was updated since you opened the form. Please review it.', {
          severity: 'info',
          autoHideDuration: 6000,
        });
      }

      // Update local state from the server result. Other questions pick up server-side changes unless
      // the client has an unsaved edit in them.
      const previousFields = fields;
      serverFields.forEach((serverField, index) => {
        if (index === fieldIndex || serverField.type === 'video' || !('value' in serverField)) return;
        const previous = previousFields[index];
        if (!previous || previous.type !== serverField.type || previous.label !== serverField.label) return;
        const previousValue = 'value' in previous ? previous.value : undefined;
        if (JSON.stringify(previousValue) === JSON.stringify(serverField.value)) return;
        const formValue = form.getValues(`fields.${index}`);
        if (JSON.stringify(formValue) === JSON.stringify(previousValue)) {
          form.setValue(`fields.${index}`, serverField.value);
        }
      });
      setFields(serverFields);

    } catch (error) {
      console.error('Error saving field:', error);
      const message = error instanceof FormChangedError
        ? error.reason === 'finalized'
          ? 'This question has been finalized and can no longer be changed.'
          : 'This form was changed since you opened it. Please reload the page and try again.'
        : 'Failed to save field';
      notifications.show(message, { severity: 'error', autoHideDuration: 6000 });
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [eventId, fields, form, notifications]);

  const getFieldValue = useCallback((fieldIndex: number) => {
    if (!fields[fieldIndex]) return undefined;
    const val = form.watch(`fields.${fieldIndex}`);
    return val;
  }, [fields, form]);

  const setFieldValue = useCallback((fieldIndex: number, value: any) => {
    if (!fields[fieldIndex]) return;
    form.setValue(`fields.${fieldIndex}`, value);
  }, [fields, form]);

  return {
    form,
    fields,
    isLoading,
    isSaving,
    saveField,
    getFieldValue,
    setFieldValue,
  };
};

export default useClientForm;
